import React, { useCallback } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import {
  IGetUserOrdersQuery,
  IGetUserOrdersQueryVariables,
  RbiOrderStatus,
  useGetUserOrdersQuery,
} from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useServiceModeLive } from 'state/service-mode';
import logger from 'utils/logger';

import { useCartHydration } from './use-cart-hydration.ts';

export const UserOrdersDefaultLimit = 5;

export const UserOrdersDefaultVariables = {
  orderStatuses: [
    RbiOrderStatus.INSERT_SUCCESSFUL,
    RbiOrderStatus.UPDATE_SUCCESSFUL,
    RbiOrderStatus.REFUND_SUCCESSFUL,
    RbiOrderStatus.REFUND_ERROR,
    RbiOrderStatus.REFUND_REQUESTED,
  ],
  shouldFilterByRegion: true,
};

export const useUserOrders = (
  variables: IGetUserOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { store } = useStoreContext();
  const { prepareReorderCartEntries } = useCartHydration({
    minimalCartEntries: [],
    cartEntries: [],
  });

  const [ordersData, setOrdersData] = React.useState<IGetUserOrdersQuery>();
  const [rawOrdersData, setRawOrdersData] = React.useState<IGetUserOrdersQuery>();
  const [fetchingMore, setFetchingMore] = React.useState<boolean>(false);
  const [isHydratingOrders, setIsHydratingOrders] = React.useState<boolean>(false);
  const [hydratedOrdersTarget, setHydratedOrdersTarget] = React.useState<string>();
  const { isAuthenticated } = useAuthContext();
  const serviceMode = useServiceModeLive();

  const hydrationTarget = [
    store.number ?? '',
    store.pos?.vendor?.toLocaleLowerCase() ?? '',
    store.isOneMenu ? 'one-menu' : 'legacy',
    serviceMode ?? '',
  ].join(':');

  const hydrateOrders = useCallback(
    async (orders: IHistoricalOrder[]) => {
      // Skip rehydration when no store is selected – the newStore would have
      // an empty storeId, which causes the RehydrateCart mutation to fail.
      if (!store.number) {
        return orders;
      }

      const result: IHistoricalOrder[] = [];

      for (const order of orders) {
        try {
          const hydratedBackendCartEntries = await prepareReorderCartEntries({
            order,
            newStore: store,
          });

          result.push(({
            ...order,
            cart: {
              ...order.cart,
              cartEntries: hydratedBackendCartEntries,
            },
          } as unknown) as IHistoricalOrder);
        } catch {
          // fallback
          result.push((order as unknown) as IHistoricalOrder);
        }
      }
      return result;
    },
    [prepareReorderCartEntries, store]
  );

  const { error, loading, refetch, fetchMore } = useGetUserOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    onCompleted: data => {
      // Keep the server response separate from target-store-specific hydration.
      // Rehydrating previously hydrated entries can retain vendor data from the old store.
      setIsHydratingOrders(true);
      setRawOrdersData(data);
    },
    context: { shouldNotBatch: true },
    skip: !isAuthenticated,
    fetchPolicy,
  });

  React.useEffect(() => {
    if (!rawOrdersData) {
      return;
    }

    let isCurrentHydration = true;
    setIsHydratingOrders(true);

    const hydrateRawOrders = async () => {
      try {
        const rawOrders = ((rawOrdersData.userOrders.orders ||
          []) as unknown) as IHistoricalOrder[];
        const hydratedOrders = await hydrateOrders(rawOrders);

        if (!isCurrentHydration) {
          return;
        }

        setOrdersData({
          ...rawOrdersData,
          userOrders: {
            ...rawOrdersData.userOrders,
            orders: hydratedOrders as any[],
          },
        });
        setHydratedOrdersTarget(hydrationTarget);
      } finally {
        if (isCurrentHydration) {
          setIsHydratingOrders(false);
        }
      }
    };

    hydrateRawOrders();

    return () => {
      // A store or service-mode change can start another hydration before this one resolves.
      // Ignore the older result so it cannot overwrite entries prepared for the latest target.
      isCurrentHydration = false;
    };
  }, [hydrateOrders, hydrationTarget, rawOrdersData]);

  const resumeAfter = ordersData?.userOrders.resumeAfter;
  const count = ordersData?.userOrders.count || 0;
  const orders = ((ordersData?.userOrders.orders || []) as unknown) as IHistoricalOrder[];

  const handleLoadMore = useCallback(async () => {
    if (!resumeAfter) {
      return;
    }

    logEvent(CustomEventNames.LOAD_MORE, EventTypes.Other, {
      'Source Page': '/recentitems',
    });

    setFetchingMore(true);

    try {
      const { data: fetchMoreResult } = await fetchMore({
        variables: { resumeAfter },
      });

      const prevOrders = rawOrdersData?.userOrders.orders || [];
      const currentOrders = fetchMoreResult.userOrders.orders || [];

      const userOrders = {
        ...fetchMoreResult,
        userOrders: {
          ...fetchMoreResult.userOrders,
          orders: [...prevOrders, ...currentOrders],
        },
      };

      setIsHydratingOrders(true);
      setRawOrdersData(userOrders as IGetUserOrdersQuery);
      return userOrders;
    } catch (error) {
      logger.warn({
        message: '[useUserOrders] Failed to fetch more recent orders',
        error,
      });
    } finally {
      setFetchingMore(false);
    }
  }, [fetchMore, rawOrdersData?.userOrders.orders, resumeAfter]);

  // Mark the data stale during the render where the hydration target changes. Waiting for the
  // effect to set loading would briefly render entries hydrated for the previous store.
  const isHydratingOrder =
    isHydratingOrders || (!!rawOrdersData && hydratedOrdersTarget !== hydrationTarget);

  return {
    error,
    loading,
    orders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
    isHydratingOrder,
  };
};
